import React, { useState, useEffect, useCallback } from "react";
import { StyleSheet, View, TouchableOpacity, Modal, Dimensions, Platform } from "react-native";
import { BlurView } from "expo-blur";
import { ThemedText } from "./ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Colors } from "@/constants/ThemeColours";

type AlertButton = {
  text: string;
  onPress?: () => void;
  style?: "default" | "cancel" | "destructive";
};

export type AlertLink = {
  url: string;
  label?: string;
  onPress: () => void;
};

type AlertConfig = {
  title: string;
  message?: string;
  buttons?: AlertButton[];
  link?: AlertLink;
  options?: { cancelable?: boolean };
};

// Global state for the alert
let alertInstance: (config: AlertConfig | null) => void = () => {};

export const CustomAlert = {
  alert: (
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: { cancelable?: boolean; link?: AlertLink }
  ) => {
    const { link, ...restOptions } = options || {};
    alertInstance({ title, message, buttons, link, options: restOptions });
  },
  dismiss: () => {
    alertInstance(null);
  },
};

export function AlertRenderer() {
  const [config, setConfig] = useState<AlertConfig | null>(null);
  const isDark = useThemeColor({}, "background") === Colors.dark.background;

  useEffect(() => {
    alertInstance = setConfig;
    return () => {
      alertInstance = () => {};
    };
  }, []);

  const handleDismiss = useCallback(() => {
    if (config?.options?.cancelable !== false) {
      setConfig(null);
    }
  }, [config]);

  if (!config) return null;

  const buttons = config.buttons || [{ text: "OK", onPress: () => {} }];

  return (
    <Modal transparent visible={!!config} animationType="none" onRequestClose={handleDismiss}>
      <View style={styles.overlay}>
        <View style={StyleSheet.absoluteFill}>
          <TouchableOpacity activeOpacity={1} style={styles.backdrop} onPress={handleDismiss}>
            <BlurView intensity={isDark ? 20 : 40} style={StyleSheet.absoluteFill} tint={isDark ? "dark" : "light"} />
          </TouchableOpacity>
        </View>

        <View
          style={[styles.alertContainer, { backgroundColor: isDark ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)" }, styles.glassEffect]}
        >
          <View style={styles.content}>
            <ThemedText type="subtitle" style={styles.title}>
              {config.title}
            </ThemedText>
            {config.message && <ThemedText style={styles.message}>{config.message}</ThemedText>}
            {config.link && (
              <TouchableOpacity
                style={styles.linkContainer}
                activeOpacity={0.7}
                onPress={() => {
                  config.link?.onPress();
                  setConfig(null);
                }}
              >
                <ThemedText style={styles.linkText}>
                  🔗 {config.link.label || config.link.url}
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          <View style={[styles.buttonContainer, buttons.length > 2 ? styles.buttonContainerVertical : styles.buttonContainerHorizontal]}>
            {buttons.map((button, index) => {
              const isDestructive = button.style === "destructive";
              const isCancel = button.style === "cancel";

              return (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.button,
                    buttons.length <= 2 && index === 0 && buttons.length > 1 && styles.buttonBorderRight,
                    buttons.length > 2 && index < buttons.length - 1 && styles.buttonBorderBottom,
                    buttons.length > 2 ? styles.buttonVertical : styles.buttonHorizontal,
                  ]}
                  onPress={() => {
                    if (button.onPress) button.onPress();
                    setConfig(null);
                  }}
                >
                  <ThemedText style={[styles.buttonText, isDestructive && styles.destructiveText, isCancel && styles.cancelText]}>{button.text}</ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const { width } = Dimensions.get("window");
const ALERT_WIDTH = Math.min(width * 0.8, 320);

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  alertContainer: {
    width: ALERT_WIDTH,
    borderRadius: 0,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.2)",
    elevation: 25,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    ...Platform.select({
      web: {
        backdropFilter: "blur(10px)",
      },
    }),
  },
  glassEffect: {
    // Subtle gradient-like feel is achieved via the background color with alpha
  },
  content: {
    paddingTop: 32,
    paddingBottom: 24,
    paddingHorizontal: 24,
    alignItems: "center",
  },
  title: {
    textAlign: "center",
    marginBottom: 12,
    fontSize: 22,
    fontWeight: "800",
    letterSpacing: -0.5,
  },
  message: {
    textAlign: "center",
    opacity: 0.7,
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "400",
  },
  buttonContainer: {
    borderTopWidth: 1,
    borderColor: "rgba(150,150,150,0.15)",
  },
  buttonContainerHorizontal: {
    flexDirection: "row",
  },
  buttonContainerVertical: {
    flexDirection: "column",
  },
  button: {
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonHorizontal: {
    flex: 1,
  },
  buttonVertical: {
    width: "100%",
  },
  buttonBorderRight: {
    borderRightWidth: 1,
    borderColor: "rgba(150,150,150,0.15)",
  },
  buttonBorderBottom: {
    borderBottomWidth: 1,
    borderColor: "rgba(150,150,150,0.15)",
  },
  buttonText: {
    fontSize: 17,
    color: "#007AFF",
    fontWeight: "600",
  },
  destructiveText: {
    color: "#FF3B30",
    fontWeight: "700",
  },
  cancelText: {
    opacity: 0.8,
    fontWeight: "500",
  },
  linkContainer: {
    marginTop: 14,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 0,
    backgroundColor: "rgba(0, 122, 255, 0.08)",
    borderWidth: 1,
    borderColor: "rgba(0, 122, 255, 0.25)",
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
  },
  linkText: {
    fontSize: 14,
    color: "#007AFF",
    fontWeight: "600",
    textDecorationLine: "underline",
    textAlign: "center",
  },
});
